import { Trans } from "@lingui/macro";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  selectTradeboxMarketAddress,
  selectTradeboxMarketInfo,
} from "context/SyntheticsStateContext/selectors/tradeboxSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { getPreferredMarketDisplayName } from "domain/synthetics/markets/display";
import { Token } from "domain/tokens";
import { IOrderBookResponse, useGetOrderBook, useGetOrderBookSpreads } from "lib/apis/markets";
import { useChainId } from "lib/chains";
import { formatUsdToNumber } from "lib/numbers";

import { Dropdown } from "components/Dropdown/Dropdown";

import OrderBookSkeleton from "./OrderBookSkeleton";
import OrderBookTable from "./OrderBookTable";
import { formatSpreadPercentage } from "./utils";
import { useChartHeaderFormattedValues } from "../../useChartHeaderFormattedValues";
import "./OrderBook.scss";

export function getMaxTotal(data: IOrderBookResponse): number {
  const allOrders = [...data.bids, ...data.asks];
  if (allOrders.length === 0) return 0;
  return Math.max(...allOrders.map((order) => parseFloat(order.totalInToken)));
}

const VISIBLE_ORDERBOOK_ROWS = 6;
const ORDERBOOK_FETCH_LIMIT = 12;

type OrderBookSnapshot = {
  key: string;
  data: IOrderBookResponse;
};

function hasRenderableDepth(data: IOrderBookResponse) {
  return data.asks.length > 0 && data.bids.length > 0;
}

function filterValidLevels(data: IOrderBookResponse | null): IOrderBookResponse {
  return {
    asks: (data?.asks || []).filter((level) => parseFloat(level.price) > 0),
    bids: (data?.bids || []).filter((level) => parseFloat(level.price) > 0),
  };
}

const OrderBook = ({ selectedToken }: { selectedToken: Token }) => {
  const [spreadValue, setSpreadValue] = useState({ label: String(0.1), value: 0.1 });
  const [snapshot, setSnapshot] = useState<OrderBookSnapshot | null>(null);
  const firstLoading = useRef(true);

  const marketInfo = useSelector(selectTradeboxMarketInfo);
  const marketAddress = useSelector(selectTradeboxMarketAddress) ?? marketInfo?.marketTokenAddress;
  const marketLabel = useMemo(
    () => getPreferredMarketDisplayName(marketInfo) ?? selectedToken?.symbol ?? "unknown",
    [marketInfo, selectedToken?.symbol]
  );
  const { avgPrice } = useChartHeaderFormattedValues();
  const { chainId } = useChainId();

  const tokenPrice = useMemo(() => formatUsdToNumber(avgPrice), [avgPrice]);

  const { data: spreadsData } = useGetOrderBookSpreads({
    indexToken: selectedToken?.symbol,
    chainId,
  });

  const spreadOptions = useMemo(() => {
    return (spreadsData?.spreads || [0.1]).map((item) => ({ value: item, label: String(item) }));
  }, [spreadsData?.spreads]);

  const defaultSpread = useMemo(() => {
    return spreadsData?.defaultSpread ?? spreadOptions[0]?.value ?? 0.1;
  }, [spreadsData?.defaultSpread, spreadOptions]);
  const requestKey = useMemo(
    () =>
      `${selectedToken?.symbol ?? "unknown"}:${chainId ?? "unknown"}:${marketAddress ?? "aggregate"}:${spreadValue.value}`,
    [chainId, marketAddress, selectedToken?.symbol, spreadValue.value]
  );

  const {
    data: dataRaw,
    isPending: isPendingRaw,
    error,
  } = useGetOrderBook(
    selectedToken?.symbol,
    {
      currentPrice: tokenPrice?.toString?.() || "0",
      limit: ORDERBOOK_FETCH_LIMIT,
      spreadValueInPrice: spreadValue.value,
      chainId,
      marketAddress,
    },
    {
      refetchInterval: 1000,
    }
  );

  useEffect(() => {
    const values = spreadOptions.map((option) => option.value);
    if (!values.includes(spreadValue.value) && values.length) {
      setSpreadValue({ label: String(defaultSpread), value: defaultSpread });
    }
  }, [defaultSpread, spreadOptions, spreadValue.value]);

  useEffect(() => {
    if (dataRaw) {
      const filtered = filterValidLevels(dataRaw);

      if (hasRenderableDepth(filtered)) {
        setSnapshot({
          key: requestKey,
          data: filtered,
        });
        return;
      }

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn("Ignoring incomplete live depth update", {
          symbol: selectedToken.symbol,
          asks: filtered.asks.length,
          bids: filtered.bids.length,
          spread: spreadValue.value,
        });
      }
      return;
    }

    if (!isPendingRaw && error && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("Live depth request failed; keeping previous snapshot", {
        symbol: selectedToken.symbol,
        spread: spreadValue.value,
        error,
      });
    }
  }, [dataRaw, error, isPendingRaw, requestKey, selectedToken.symbol, spreadValue.value]);

  const isLoading = useMemo(() => {
    if (firstLoading.current && isPendingRaw) {
      firstLoading.current = false;
      return true;
    }
    return !snapshot && isPendingRaw;
  }, [isPendingRaw, snapshot]);

  // Simulation disabled: order book now renders backend-derived live depth only.
  const liveOrderBook = useMemo(() => filterValidLevels(snapshot?.data ?? null), [snapshot]);

  const maxTotal = useMemo(() => {
    return liveOrderBook ? getMaxTotal(liveOrderBook) : 0;
  }, [liveOrderBook]);
  const visibleAsks = useMemo(() => liveOrderBook?.asks?.slice(0, VISIBLE_ORDERBOOK_ROWS) || [], [liveOrderBook]);
  const visibleBids = useMemo(() => liveOrderBook?.bids?.slice(0, VISIBLE_ORDERBOOK_ROWS) || [], [liveOrderBook]);

  return (
    <div className="OrderBook">
      <div className="OrderBook-head">
        <Dropdown
          options={spreadOptions}
          selectedOption={spreadValue}
          onSelect={setSpreadValue}
          className="OrderBook-dropdown"
          classNameDropdownCurrent="OrderBook-dropdown-current"
        />
        <span className="OrderBook-symbol">{marketLabel}</span>
      </div>
      <div className="OrderBook-columns">
        <span>
          <Trans>Price</Trans>
        </span>
        <span>
          <Trans>Size({selectedToken?.symbol})</Trans>
        </span>
        <span className="text-right">
          <Trans>Total({selectedToken?.symbol})</Trans>
        </span>
      </div>
      {!isLoading && (
        <OrderBookTable maxTotal={maxTotal} orders={visibleAsks} side="asks" targetRowCount={VISIBLE_ORDERBOOK_ROWS} />
      )}
      {isLoading && <OrderBookSkeleton />}
      <div className="OrderBook-spread">
        <span>
          <Trans>Spread</Trans>
        </span>
        <span>{spreadValue.value}</span>
        <span className="text-right">{formatSpreadPercentage(Number(spreadValue.value), Number(tokenPrice))}</span>
      </div>
      {!isLoading && (
        <OrderBookTable maxTotal={maxTotal} orders={visibleBids} side="bids" targetRowCount={VISIBLE_ORDERBOOK_ROWS} />
      )}
      {isLoading && <OrderBookSkeleton />}
    </div>
  );
};

export default OrderBook;
